import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Download, FileArchive, CheckCircle2 } from "lucide-react";
import { useState, useEffect } from "react";

interface ZipDownloadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  zipLink: string;
}

export function ZipDownloadDialog({ open, onOpenChange, zipLink }: ZipDownloadDialogProps) {
  const [downloadStarted, setDownloadStarted] = useState(false);

  useEffect(() => {
    if (open) {
      setDownloadStarted(false);
      // Auto start download after a brief moment
      const timer = setTimeout(() => {
        setDownloadStarted(true);
        window.open(zipLink, "_blank");
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [open, zipLink]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileArchive className="w-6 h-6 text-primary" />
            Preparando seu Download...
          </DialogTitle>
          <DialogDescription className="text-muted-foreground pt-4 space-y-4 text-left">
            <p>O seu álbum completo está sendo baixado em um arquivo compactado (ZIP) para manter a qualidade original e agilizar o download de todas as fotos.</p>
            
            <div className="bg-foreground/5 p-4 rounded-lg space-y-3 border border-foreground/10 mt-4">
              <h4 className="font-bold text-foreground uppercase tracking-widest text-xs flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500" />
                Como descompactar?
              </h4>
              <ul className="text-[13px] space-y-3 text-muted-foreground list-disc pl-5">
                <li>
                  <strong className="text-foreground">No Celular (iPhone ou Android):</strong><br/>
                  Vá no aplicativo "Arquivos" ou "Meus Arquivos", encontre o arquivo ZIP baixado na pasta Downloads e toque nele. O celular irá extrair uma pasta com todas as suas fotos automaticamente.
                </li>
                <li>
                  <strong className="text-foreground">No Computador:</strong><br/>
                  Clique com o botão direito sobre o arquivo baixado e selecione "Extrair tudo..." ou "Extrair aqui".
                </li>
              </ul>
            </div>
          </DialogDescription>
        </DialogHeader>

        <DialogFooter className="mt-6 sm:justify-center">
          <Button 
            className="w-full bg-primary text-black hover:bg-primary/90 font-bold tracking-widest transition-transform hover:scale-105 active:scale-95 h-12"
            onClick={() => {
              setDownloadStarted(true);
              window.open(zipLink, "_blank");
            }}
          >
            <Download className="w-4 h-4 mr-2" />
            {downloadStarted ? "BAIXAR NOVAMENTE" : "INICIAR DOWNLOAD AGORA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
